import React from 'react'
import { Box, Typography } from '@mui/material'
import { Lock as LockIcon } from '@mui/icons-material'
import { AisleCell, GridCell } from '../types'
import { ViewMode, MapViewMode, CELL_ALPHA } from '../config/constants'
import { resolveCellVisual, withAlpha } from '../config/utils'
import { mapCellStyles as styles } from '../styles/MapCell.styles'
import { slotMarkerSx } from '../styles/shared.styles'
import { MAP_CELL_ATTR } from '../hooks/useCellSummary'

/**
 * Memoizado a propósito: la grilla puede tener más de mil celdas y abrir un
 * resumen cambia el estado del padre. Sin `memo`, cada click re-renderiza todas
 * las celdas y cada `sx` vuelve a pasar por emotion, con una demora perceptible.
 * Para que el memo sirva, `onClick` debe tener identidad estable en el padre;
 * por eso recibe `cellKey` en vez de una clausura por celda.
 *
 * Tampoco monta un Tooltip: como sólo puede haber un resumen abierto, la grilla
 * renderiza un único Popper. Con un Tooltip por celda eran más de mil
 * instancias con sus hooks y su clonado de hijos sólo para tener una visible.
 */
export const MapCell = React.memo(function MapCell({
  cell,
  cellKey,
  size,
  mode = MapViewMode.brand,
  brandColors,
  zoneColors,
  highlightBrand = null,
  isSummaryOpen = false,
  onClick,
  onDoubleClick,
}: {
  cell: GridCell | AisleCell
  cellKey: string
  size: number
  mode?: ViewMode
  brandColors: Map<string, string>
  zoneColors: Map<string, string>
  highlightBrand?: string | null
  isSummaryOpen?: boolean
  /** Click simple: muestra el resumen. Recibe el nodo para anclar el Popper. */
  onClick?: (cellKey: string, anchorEl: HTMLElement) => void
  /** Doble click: abre el detalle. El padre decide si la celda lo admite. */
  onDoubleClick?: (cellKey: string) => void
}) {
  const isBlocked = cell.blockType !== 'none'
  const isDimmed = highlightBrand !== null && !cell.brands.some((b) => b.name === highlightBrand)

  const { bg, text: textValue, textColor } = resolveCellVisual(cell, mode, brandColors, zoneColors)

  return (
    <Box
      {...{ [MAP_CELL_ATTR]: true }}
      onClick={onClick ? (e) => onClick(cellKey, e.currentTarget) : undefined}
      onDoubleClick={onDoubleClick ? () => onDoubleClick(cellKey) : undefined}
      sx={styles.cell({
        size,
        background: withAlpha(bg, CELL_ALPHA),
        isDimmed,
        isSummaryOpen,
        hoverBrightness: 1.15,
      })}
      >
        {cell.isConfigured && textValue !== null && (
          <Typography variant='caption' sx={styles.stockText(textColor)}>
            {textValue}
          </Typography>
        )}
        {isBlocked && (
          <LockIcon
            sx={styles.lockIcon(
              textColor === '#fff' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.35)'
            )}
          />
        )}
        {cell.hasSlots && <Box sx={slotMarkerSx(8)} />}
    </Box>
  )
})
